import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material'
import { Edit } from '@mui/icons-material'
import { Form, Formik, FormikProps } from 'formik'
import { inject } from 'mobx-react'
import React from 'react'
import * as Yup from 'yup'
import FullWidthField from 'src/common/FullWidthField'
import ResponsiveAppBar from 'src/common/ResponsiveAppBar'
import { IStores } from 'src/Stores'
import { calculateLineItem } from 'src/services/QuoteService'
import { IQuoteDTO } from 'src/viewModels/quotes/QuoteDTO'
import { IAnesthesiaLineItemDTO } from 'src/viewModels/quotes/AnesthesiaLineItemDTO'
import { IMiscellaneousLineItemDTO } from 'src/viewModels/quotes/MiscellaneousLineItemDTO'
import { IPhysicalTherapyLineItemDTO } from 'src/viewModels/quotes/PhysicalTherapyLineItemDTO'
import { IProfessionalComponentLineItemDTO } from 'src/viewModels/quotes/ProfessionalComponentLineItemDTO'
import { ITechnicalComponentLineItemDTO } from 'src/viewModels/quotes/TechnicalComponentLineItemDTO'
import { IQuoteCalculationRequest } from 'src/viewModels/QuoteCalculationRequest'
import { CostSourceText } from './CostSourceText'
import { ITypeDto } from 'src/generated_client'
import { QuoteStatus } from 'src/utils/QuoteStatus'

type EditableLineItem =
  | IProfessionalComponentLineItemDTO
  | ITechnicalComponentLineItemDTO
  | IAnesthesiaLineItemDTO
  | IPhysicalTherapyLineItemDTO
  | IMiscellaneousLineItemDTO

interface IEditQuoteLineModalProps {
  isOpen?: boolean
  quote?: IQuoteDTO
  lineItem?: EditableLineItem
  lineItemIndex?: number
  lineItemType?: string
  reloadParent?: () => void
  closeModal?: () => void
  isNum?: (str: any) => boolean
  genericTypes?: ITypeDto[]
  getTypesForModal?: (type: string) => void
}

interface IEditFormValues {
  cptCode: string
  actualCost: string
  contractMultiplier: string
  multipleProcedureDiscount: string
  bilateral: boolean
  otherDescription: string
  monitoredMultiplier: string
  numberOfVisits: string
  costPerVisit: string
  actualCostPerVisit: string
  lineDescription: string
  typeId: string
  reconciledOverrideCost: string
  reasonCode: string
}

interface IEditQuoteLineModalState {
  isLoading: boolean
  selectedCostSource: string | undefined
  calculatedCost: number | undefined
  reconciledCalculatedCost: number | undefined
}

class EditQuoteLineModal extends React.Component<
  IEditQuoteLineModalProps,
  IEditQuoteLineModalState
> {
  private formRef = React.createRef<FormikProps<IEditFormValues>>()

  public state: IEditQuoteLineModalState = {
    isLoading: false,
    selectedCostSource: undefined,
    calculatedCost: undefined,
    reconciledCalculatedCost: undefined,
  }

  public componentDidUpdate(prevProps: IEditQuoteLineModalProps) {
    if (this.props.lineItem !== prevProps.lineItem && this.props.lineItem) {
      const item = this.props.lineItem as any
      this.setState({
        selectedCostSource: item.costSource != null ? String(item.costSource) : undefined,
        calculatedCost: item.cost,
        reconciledCalculatedCost: item.reconciledCost,
      })
      const { lineItemType, getTypesForModal } = this.props
      if (lineItemType && lineItemType !== 'PT' && getTypesForModal) {
        getTypesForModal(lineItemType)
      }
    }
  }

  private setSelectedCostSource = (event: SelectChangeEvent<string>) => {
    const target = event.target as any
    const value =
      target.selectedOptions && target.selectedOptions[0]
        ? target.selectedOptions[0].value
        : event.target.value
    this.setState({ selectedCostSource: value })
  }

  private buildCalculationRequest = (
    values: IEditFormValues,
    isRecon = false
  ): IQuoteCalculationRequest | null => {
    const { lineItemType } = this.props

    if (lineItemType === 'PT') return null

    return {
      actualCost: values.actualCost ? +values.actualCost : 0,
      contractMultiplier: values.contractMultiplier
        ? +values.contractMultiplier
        : undefined,
      multipleProcedureMultiplier: values.multipleProcedureDiscount
        ? +values.multipleProcedureDiscount
        : undefined,
      bilateral: values.bilateral,
      monitoredMultiplier:
        lineItemType === 'AN' && values.monitoredMultiplier
          ? +values.monitoredMultiplier
          : undefined,
      isRecon,
    }
  }

  private handleCalculate = async (
    isRecon = false,
    overrides?: Partial<IEditFormValues>
  ) => {
    const formValues = this.formRef.current?.values
    if (!formValues) return

    const values = overrides ? { ...formValues, ...overrides } : formValues

    if (this.props.lineItemType === 'PT') {
      const cost = (+values.numberOfVisits || 0) * (+values.costPerVisit || 0)
      if (isRecon) {
        this.setState({ reconciledCalculatedCost: cost })
      } else {
        this.setState({ calculatedCost: cost })
      }
      return
    }

    const req = this.buildCalculationRequest(values, isRecon)
    if (!req) return

    this.setState({ isLoading: true })
    try {
      const result = await calculateLineItem(req)
      if (isRecon) {
        this.setState({ reconciledCalculatedCost: result.reconciledCost ?? result.cost })
      } else {
        this.setState({ calculatedCost: result.cost })
      }
    } finally {
      this.setState({ isLoading: false })
    }
  }

  private handleSave = async () => {
    const { lineItem, lineItemIndex, lineItemType, quote, reloadParent, closeModal } =
      this.props
    if (lineItem == null || lineItemIndex == null || !quote) return

    const values = this.formRef.current?.values
    if (!values) return

    const isValid = await this.formRef.current!.validateForm()
    if (Object.keys(isValid).length > 0) {
      this.formRef.current!.setTouched(
        Object.keys(isValid).reduce((acc, k) => ({ ...acc, [k]: true }), {})
      )
      return
    }

    const costSource =
      this.state.selectedCostSource != null ? +this.state.selectedCostSource : undefined
    const isReconMode =
      this.props.quote?.status == QuoteStatus.NeedsRecon ||
      this.props.quote?.status == QuoteStatus.Reconciled
    // In recon mode, never overwrite the quoted cost — only reconciledCost is updated
    const cost = isReconMode
      ? (lineItem as any).cost
      : (this.state.calculatedCost ?? (lineItem as any).cost ?? 0)
    const reconciledCost =
      this.state.reconciledCalculatedCost ?? (lineItem as any).reconciledCost

    const selectedType = this.props.genericTypes?.find((t) => t.id === values.typeId)

    switch (lineItemType) {
      case 'PC': {
        const arr = quote.professionalComponentLineItems!
        const existing = arr[lineItemIndex] as IProfessionalComponentLineItemDTO
        arr[lineItemIndex] = {
          ...existing,
          cptCode: values.cptCode,
          actualCost: values.actualCost ? +values.actualCost : existing.actualCost,
          percentageOfContract: values.contractMultiplier
            ? +values.contractMultiplier
            : existing.percentageOfContract,
          multipleProcedurePercentage: values.multipleProcedureDiscount
            ? +values.multipleProcedureDiscount
            : existing.multipleProcedurePercentage,
          bilateral: values.bilateral,
          costSource,
          otherDescription: values.otherDescription || undefined,
          typeId: values.typeId || existing.typeId,
          typeText: selectedType ? selectedType.name : existing.typeText,
          cost,
          ...(isReconMode && {
            ...(existing.reconciledCost != undefined ? { reconciledCost } : {}),
            reconciledOverrideCost: values.reconciledOverrideCost
              ? +values.reconciledOverrideCost
              : existing.reconciledOverrideCost,
            reasonCode: values.reasonCode || existing.reasonCode,
          }),
        }
        break
      }
      case 'TC': {
        const arr = quote.technicalComponentLineItems!
        const existing = arr[lineItemIndex] as ITechnicalComponentLineItemDTO
        arr[lineItemIndex] = {
          ...existing,
          cptCode: values.cptCode,
          actualCost: values.actualCost ? +values.actualCost : existing.actualCost,
          percentageOfContract: values.contractMultiplier
            ? +values.contractMultiplier
            : existing.percentageOfContract,
          multipleProcedurePercentage: values.multipleProcedureDiscount
            ? +values.multipleProcedureDiscount
            : existing.multipleProcedurePercentage,
          bilateral: values.bilateral,
          costSource,
          otherDescription: values.otherDescription || undefined,
          typeId: values.typeId || existing.typeId,
          typeText: selectedType ? selectedType.name : existing.typeText,
          cost,
          ...(isReconMode && {
            ...(existing.reconciledCost != undefined ? { reconciledCost } : {}),
            reconciledOverrideCost: values.reconciledOverrideCost
              ? +values.reconciledOverrideCost
              : existing.reconciledOverrideCost,
            reasonCode: values.reasonCode || existing.reasonCode,
          }),
        }
        break
      }
      case 'AN': {
        const arr = quote.anesthesiaLineItems!
        const existing = arr[lineItemIndex] as IAnesthesiaLineItemDTO
        arr[lineItemIndex] = {
          ...existing,
          cptCode: values.cptCode,
          actualCost: values.actualCost ? +values.actualCost : existing.actualCost,
          percentageOfContract: values.contractMultiplier
            ? +values.contractMultiplier
            : existing.percentageOfContract,
          multipleProcedurePercentage: values.multipleProcedureDiscount
            ? +values.multipleProcedureDiscount
            : existing.multipleProcedurePercentage,
          bilateral: values.bilateral,
          costSource,
          otherDescription: values.otherDescription || undefined,
          monitoredMultiplier: values.monitoredMultiplier
            ? +values.monitoredMultiplier
            : existing.monitoredMultiplier,
          typeId: values.typeId || existing.typeId,
          typeText: selectedType ? selectedType.name : existing.typeText,
          cost,
          ...(isReconMode && {
            ...(existing.reconciledCost != undefined ? { reconciledCost } : {}),
            reconciledOverrideCost: values.reconciledOverrideCost
              ? +values.reconciledOverrideCost
              : existing.reconciledOverrideCost,
            reasonCode: values.reasonCode || existing.reasonCode,
          }),
        }
        break
      }
      case 'PT': {
        const arr = quote.physicalTherapyLineItems!
        const existing = arr[lineItemIndex] as IPhysicalTherapyLineItemDTO
        arr[lineItemIndex] = {
          ...existing,
          numberOfVisits: values.numberOfVisits
            ? +values.numberOfVisits
            : existing.numberOfVisits,
          costPerVisit: values.costPerVisit
            ? +values.costPerVisit
            : existing.costPerVisit,
          actualCostPerVisit: values.actualCostPerVisit
            ? +values.actualCostPerVisit
            : existing.actualCostPerVisit,
          cost,
          ...(isReconMode && {
            ...(existing.reconciledCost != undefined ? { reconciledCost } : {}),
          }),
        }
        break
      }
      case 'MI': {
        const arr = quote.miscellaneousLineItems!
        const existing = arr[lineItemIndex] as IMiscellaneousLineItemDTO
        arr[lineItemIndex] = {
          ...existing,
          cptCode: values.cptCode,
          lineDescription: values.lineDescription,
          actualCost: values.actualCost ? +values.actualCost : existing.actualCost,
          percentageOfContract: values.contractMultiplier
            ? +values.contractMultiplier
            : existing.percentageOfContract,
          multipleProcedurePercentage: values.multipleProcedureDiscount
            ? +values.multipleProcedureDiscount
            : existing.multipleProcedurePercentage,
          bilateral: values.bilateral,
          costSource,
          otherDescription: values.otherDescription || undefined,
          typeId: values.typeId || existing.typeId,
          typeText: selectedType ? selectedType.name : existing.typeText,
          cost,
          ...(isReconMode && {
            ...(existing.reconciledCost != undefined ? { reconciledCost } : {}),
            reconciledOverrideCost: values.reconciledOverrideCost
              ? +values.reconciledOverrideCost
              : existing.reconciledOverrideCost,
            reasonCode: values.reasonCode || existing.reasonCode,
          }),
        }
        break
      }
    }

    reloadParent?.()
    closeModal?.()
  }

  private buildInitialValues = (): IEditFormValues => {
    const item = this.props.lineItem as any
    if (!item) {
      return {
        cptCode: '',
        actualCost: '',
        contractMultiplier: '',
        multipleProcedureDiscount: '',
        bilateral: false,
        otherDescription: '',
        monitoredMultiplier: '',
        numberOfVisits: '',
        costPerVisit: '',
        actualCostPerVisit: '',
        lineDescription: '',
        typeId: '',
        reconciledOverrideCost: '',
        reasonCode: '',
      }
    }
    return {
      cptCode: item.cptCode ?? '',
      actualCost: item.actualCost != null ? String(item.actualCost) : '',
      contractMultiplier:
        item.percentageOfContract != null ? String(item.percentageOfContract) : '',
      multipleProcedureDiscount:
        item.multipleProcedurePercentage != null
          ? String(item.multipleProcedurePercentage)
          : '',
      bilateral: item.bilateral ?? false,
      otherDescription: item.otherDescription ?? '',
      monitoredMultiplier:
        item.monitoredMultiplier != null ? String(item.monitoredMultiplier) : '',
      numberOfVisits: item.numberOfVisits != null ? String(item.numberOfVisits) : '',
      costPerVisit: item.costPerVisit != null ? String(item.costPerVisit) : '',
      actualCostPerVisit:
        item.actualCostPerVisit != null ? String(item.actualCostPerVisit) : '',
      lineDescription: item.lineDescription ?? '',
      typeId: item.typeId ?? '',
      reconciledOverrideCost: '',
      reasonCode: item.reasonCode ?? '',
    }
  }

  private buildValidationSchema = () => {
    const { lineItemType } = this.props
    if (lineItemType === 'PT') {
      return Yup.object().shape({
        numberOfVisits: Yup.number().typeError('Must be a number').required('Required'),
        costPerVisit: Yup.number().typeError('Must be a number').required('Required'),
        actualCostPerVisit: Yup.number()
          .typeError('Must be a number')
          .required('Required'),
      })
    }
    if (lineItemType === 'MI') {
      return Yup.object().shape({
        lineDescription: Yup.string().required('Line Description is required'),
        actualCost: Yup.number().typeError('Must be a number').required('Required'),
      })
    }
    return Yup.object().shape({
      actualCost: Yup.number().typeError('Must be a number').required('Required'),
    })
  }

  private getModalTitle = () => {
    const map: Record<string, string> = {
      PC: 'Edit Professional Component Line',
      TC: 'Edit Technical Component Line',
      AN: 'Edit Anesthesia Line',
      PT: 'Edit Physical Therapy Line',
      MI: 'Edit Miscellaneous Line',
    }
    const base = map[this.props.lineItemType ?? ''] ?? 'Edit Line Item'
    return base
  }

  public render() {
    const { isOpen, lineItemType, lineItem, genericTypes = [] } = this.props
    const { isLoading, selectedCostSource, calculatedCost, reconciledCalculatedCost } =
      this.state
    const item = lineItem as any
    const isPT = lineItemType === 'PT'
    const isReconMode =
      this.props.quote?.status == QuoteStatus.NeedsRecon ||
      this.props.quote?.status == QuoteStatus.Reconciled
    const showTypes = !isPT

    return (
      <Dialog
        fullWidth
        maxWidth="lg"
        open={isOpen ?? false}
        onClose={(_event, reason) => {
          if (reason === 'backdropClick') return
          this.props.closeModal?.()
        }}
      >
        <DialogTitle style={{ padding: 0 }}>
          <ResponsiveAppBar
            title={this.getModalTitle()}
            pageIcon={<Edit />}
            isModal={true}
          />
        </DialogTitle>
        <Formik
          key={`${lineItemType}-${item?.id ?? lineItem?.toString()}`}
          initialValues={this.buildInitialValues()}
          validationSchema={this.buildValidationSchema()}
          onSubmit={() => {}}
          innerRef={this.formRef}
          enableReinitialize
        >
          {({ values, setFieldValue }) => (
            <>
              <DialogContent style={{ paddingTop: 16 }}>
                {isLoading && (
                  <div
                    style={{
                      position: 'fixed',
                      display: 'flex',
                      width: '100%',
                      height: '100%',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: 'rgba(0,0,0,0.5)',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 1300,
                    }}
                  >
                    <CircularProgress size={80} />
                  </div>
                )}

                {/* Read-only provider info */}
                <Grid container spacing={1} style={{ marginBottom: 12 }}>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">
                      <strong>Provider:</strong> {item?.providerName ?? '—'}
                      {item?.physicianName ? `  |  Physician: ${item.physicianName}` : ''}
                      {item?.typeText ? `  |  Type: ${item.typeText}` : ''}
                    </Typography>
                  </Grid>
                </Grid>

                <Form>
                  <Grid container spacing={2}>
                    {/* PT-specific fields */}
                    {isPT ? (
                      <>
                        <Grid
                          item
                          xs={4}
                          onBlur={() => this.handleCalculate(isReconMode)}
                        >
                          <FullWidthField
                            name="numberOfVisits"
                            label="# of Visits"
                            variant="outlined"
                            type="number"
                          />
                        </Grid>
                        <Grid item xs={4}>
                          <FullWidthField
                            name="actualCostPerVisit"
                            label="Est. U&C Per Visit"
                            variant="outlined"
                            type="number"
                          />
                        </Grid>
                        <Grid
                          item
                          xs={4}
                          onBlur={() => this.handleCalculate(isReconMode)}
                        >
                          <FullWidthField
                            name="costPerVisit"
                            label="Quoted Per Visit"
                            variant="outlined"
                            type="number"
                          />
                        </Grid>

                        {/* PT Calculated value */}
                        <Grid item xs={4}>
                          <TextField
                            label={isReconMode ? 'Reconciled Cost' : 'Quoted Cost'}
                            value={
                              isReconMode
                                ? reconciledCalculatedCost != null
                                  ? new Intl.NumberFormat('en-US', {
                                      style: 'currency',
                                      currency: 'USD',
                                    }).format(reconciledCalculatedCost)
                                  : ''
                                : calculatedCost != null
                                  ? new Intl.NumberFormat('en-US', {
                                      style: 'currency',
                                      currency: 'USD',
                                    }).format(calculatedCost)
                                  : ''
                            }
                            disabled
                            fullWidth
                            variant="outlined"
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                      </>
                    ) : (
                      <>
                        {/* Type dropdown (any mode, non-PT) */}
                        {showTypes && (
                          <Grid item xs={4}>
                            <FormControl fullWidth variant="outlined">
                              <InputLabel shrink>Type</InputLabel>
                              <Select
                                variant="outlined"
                                label="Type"
                                value={values.typeId}
                                onChange={(e) => setFieldValue('typeId', e.target.value)}
                                displayEmpty
                              >
                                <MenuItem value="">—</MenuItem>
                                {genericTypes.map((t) => (
                                  <MenuItem key={t.id} value={t.id}>
                                    {t.name}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                        )}

                        {/* CPT Code */}
                        <Grid item xs={4}>
                          <FullWidthField
                            name="cptCode"
                            label="CPT Code"
                            variant="outlined"
                            disabled={isReconMode}
                          />
                        </Grid>

                        {/* MI Line Description */}
                        {lineItemType === 'MI' && (
                          <Grid item xs={4}>
                            <FullWidthField
                              name="lineDescription"
                              label="Line Description"
                              variant="outlined"
                            />
                          </Grid>
                        )}

                        {/* Cost Source */}
                        <Grid item xs={4}>
                          <FormControl fullWidth variant="outlined">
                            <InputLabel shrink>Cost Source</InputLabel>
                            <Select
                              variant="outlined"
                              label="Cost Source"
                              value={selectedCostSource ?? ''}
                              onChange={this.setSelectedCostSource}
                              displayEmpty
                            >
                              {Object.keys(CostSourceText).map((key) => (
                                <MenuItem key={key} value={key}>
                                  {CostSourceText[key]}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>

                        {/* Other Description (when cost source = Other) */}
                        {CostSourceText[selectedCostSource ?? ''] === 'Other' && (
                          <Grid item xs={8}>
                            <FullWidthField
                              name="otherDescription"
                              label="Other Description"
                              variant="outlined"
                            />
                          </Grid>
                        )}

                        {/* Billed Charges */}
                        <Grid
                          item
                          xs={4}
                          onBlur={() => this.handleCalculate(isReconMode)}
                        >
                          <FullWidthField
                            name="actualCost"
                            label="Billed Charges"
                            variant="outlined"
                            type="number"
                            disabled={isReconMode}
                          />
                        </Grid>

                        {/* Contract % */}
                        <Grid
                          item
                          xs={4}
                          onBlur={() => this.handleCalculate(isReconMode)}
                        >
                          <FullWidthField
                            name="contractMultiplier"
                            label="% of Contract"
                            variant="outlined"
                            type="number"
                          />
                        </Grid>

                        {/* MPPD dropdown */}
                        <Grid item xs={4}>
                          <FormControl fullWidth variant="outlined">
                            <InputLabel shrink>MPPD</InputLabel>
                            <Select
                              variant="outlined"
                              label="MPPD"
                              value={values.multipleProcedureDiscount}
                              onChange={(e) => {
                                const value = e.target.value as string
                                setFieldValue('multipleProcedureDiscount', value)
                                this.handleCalculate(isReconMode, {
                                  multipleProcedureDiscount: value,
                                })
                              }}
                              displayEmpty
                            >
                              <MenuItem value="">—</MenuItem>
                              <MenuItem value="25">25%</MenuItem>
                              <MenuItem value="50">50%</MenuItem>
                              <MenuItem value="100">100%</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>

                        {/* Calculated value (read-only) */}
                        <Grid item xs={4}>
                          <TextField
                            label={isReconMode ? 'Reconciled Cost' : 'Quoted Cost'}
                            value={
                              isReconMode
                                ? reconciledCalculatedCost != null
                                  ? new Intl.NumberFormat('en-US', {
                                      style: 'currency',
                                      currency: 'USD',
                                    }).format(reconciledCalculatedCost)
                                  : ''
                                : calculatedCost != null
                                  ? new Intl.NumberFormat('en-US', {
                                      style: 'currency',
                                      currency: 'USD',
                                    }).format(calculatedCost)
                                  : ''
                            }
                            disabled
                            fullWidth
                            variant="outlined"
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>

                        {/* Bilateral */}
                        <Grid item xs={4}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={values.bilateral}
                                onChange={(e) => {
                                  const checked = e.target.checked
                                  setFieldValue('bilateral', checked)
                                  this.handleCalculate(isReconMode, {
                                    bilateral: checked,
                                  })
                                }}
                              />
                            }
                            label="Bilateral"
                          />
                        </Grid>

                        {/* AN: Monitored multiplier */}
                        {lineItemType === 'AN' && (
                          <Grid
                            item
                            xs={4}
                            onBlur={() => this.handleCalculate(isReconMode)}
                          >
                            <FullWidthField
                              name="monitoredMultiplier"
                              label="Monitored Multiplier"
                              variant="outlined"
                              type="number"
                            />
                          </Grid>
                        )}

                        {/* Recon-mode only fields */}
                        {isReconMode && (
                          <Grid
                            item
                            xs={4}
                            onBlur={() => {
                              const val =
                                this.formRef.current?.values.reconciledOverrideCost
                              if (val) {
                                this.setState({ reconciledCalculatedCost: +val })
                              } else {
                                this.handleCalculate(true)
                              }
                            }}
                          >
                            <FullWidthField
                              name="reconciledOverrideCost"
                              label="Reconciled Override"
                              variant="outlined"
                              type="number"
                            />
                          </Grid>
                        )}
                      </>
                    )}
                  </Grid>
                </Form>
              </DialogContent>
              <DialogActions>
                {isReconMode && !isPT && (
                  <TextField
                    size="small"
                    label="Reason Code"
                    value={values.reasonCode}
                    onChange={(e) => setFieldValue('reasonCode', e.target.value)}
                    variant="outlined"
                    style={{ minWidth: 180 }}
                    InputLabelProps={{ shrink: true }}
                  />
                )}
                <Button onClick={this.props.closeModal}>Cancel</Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={this.handleSave}
                  disabled={isLoading}
                >
                  Save
                </Button>
              </DialogActions>
            </>
          )}
        </Formik>
      </Dialog>
    )
  }
}

export default inject<
  IStores,
  IEditQuoteLineModalProps,
  Partial<IEditQuoteLineModalProps>,
  any
>((stores: IStores) => ({
  isOpen: stores.quotes.isEditQuoteLineModalOpen,
  quote: stores.quotes.selectedQuote,
  lineItem: stores.quotes.editingLineItem,
  lineItemIndex: stores.quotes.editingLineItemIndex,
  lineItemType: stores.quotes.editingLineItemType,
  closeModal: stores.quotes.closeEditQuoteLineModal,
  genericTypes: stores.quotes.genericTypes,
  getTypesForModal: stores.quotes.getTypesForModal,
}))(EditQuoteLineModal)
